import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Search, Save, X, RotateCcw, Edit3, BookOpen, ClipboardCheck, Video, FileText, Calculator, Layers } from "lucide-react";

const TYPE_ICONS = { reading: BookOpen, quiz: ClipboardCheck, video: Video, "study-doc": FileText, "sample-test": Calculator };
const TYPE_LABELS = { reading: "Reading", quiz: "Knowledge Check", video: "Video Lecture", "study-doc": "Study Document", "sample-test": "Sample Test" };

export default function AdminLessonsEditor() {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moduleFilter, setModuleFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [editingLesson, setEditingLesson] = useState(null);

  async function load() {
    setLoading(true);
    const { data } = await api.get("/admin/lessons");
    setLessons(data.lessons);
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => lessons.filter((l) => {
    if (moduleFilter !== "all" && l.module_id !== parseInt(moduleFilter)) return false;
    if (query && !l.title.toLowerCase().includes(query.toLowerCase()) && !l.id.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  }), [lessons, moduleFilter, query]);

  // Group by module
  const grouped = useMemo(() => {
    const g = {};
    for (const l of filtered) {
      g[l.module_id] = g[l.module_id] || { title: l.module_title, lessons: [] };
      g[l.module_id].lessons.push(l);
    }
    return g;
  }, [filtered]);

  return (
    <div data-testid="admin-lessons-editor">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex-1 min-w-[220px] relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" strokeWidth={1.5} />
          <input
            type="text"
            placeholder="Search lesson title or ID (1-1, 3-6)…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="l-search"
            className="w-full pl-9 pr-3 py-2 text-sm border border-[var(--dw-navy)]/15 rounded-sm focus:outline-none focus:border-[var(--dw-navy)]"
          />
        </div>
        <select
          value={moduleFilter}
          onChange={(e) => setModuleFilter(e.target.value)}
          data-testid="l-module-filter"
          className="text-sm border border-[var(--dw-navy)]/15 rounded-sm px-3 py-2"
        >
          <option value="all">All modules</option>
          {[1, 2, 3, 4, 5].map((m) => <option key={m} value={m}>Module {m}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-neutral-500 py-10 text-center">Loading lessons…</div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([mid, g]) => (
            <div key={mid}>
              <div className="flex items-center gap-2 mb-3">
                <Layers className="w-4 h-4 text-[var(--dw-gold-soft)]" strokeWidth={1.5} />
                <div className="overline text-[var(--dw-gold-soft)] text-[11px]">Module {mid}</div>
                <div className="font-serif text-lg text-[var(--dw-navy)]">{g.title}</div>
              </div>
              <div className="space-y-1.5">
                {g.lessons.map((l) => (
                  <LessonRow key={l.id} lesson={l} onEdit={() => setEditingLesson(l.id)} onReload={load} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {editingLesson && (
        <LessonEditorModal
          lessonId={editingLesson}
          onClose={() => setEditingLesson(null)}
          onSaved={() => { setEditingLesson(null); load(); }}
        />
      )}
    </div>
  );
}

function LessonRow({ lesson, onEdit, onReload }) {
  const Icon = TYPE_ICONS[lesson.type] || BookOpen;

  async function reset() {
    if (!window.confirm(`Reset "${lesson.title}" to the original seed content?`)) return;
    await api.post(`/admin/lessons/${lesson.id}/reset`);
    onReload();
  }

  return (
    <div className="border border-[var(--dw-navy)]/12 rounded-sm p-3 bg-white flex items-center gap-3" data-testid={`l-row-${lesson.id}`}>
      <div className="w-8 h-8 shrink-0 flex items-center justify-center border border-[var(--dw-navy)]/12 rounded-sm">
        <Icon className="w-3.5 h-3.5 text-[var(--dw-navy)]" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-mono-plex text-xs text-neutral-500">{lesson.id}</span>
          <span className="text-[10px] px-1.5 py-0.5 border border-[var(--dw-navy)]/15 rounded-sm text-neutral-600">{TYPE_LABELS[lesson.type] || lesson.type}</span>
          <span className="text-[10px] text-neutral-500">{lesson.duration_min}min</span>
          {lesson._edited && <span className="text-[10px] px-1.5 py-0.5 border border-amber-400 bg-amber-50 text-amber-800 rounded-sm inline-flex items-center gap-1"><Edit3 className="w-2.5 h-2.5" /> Edited</span>}
        </div>
        <div className="text-sm text-[var(--dw-navy)] truncate mt-0.5">{lesson.title}</div>
      </div>
      <div className="flex gap-1 shrink-0">
        {lesson._edited && (
          <button onClick={reset} title="Reset to default" data-testid={`l-reset-${lesson.id}`} className="w-8 h-8 flex items-center justify-center border border-[var(--dw-navy)]/12 hover:border-amber-500 rounded-sm text-neutral-500 hover:text-amber-600">
            <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
          </button>
        )}
        <button onClick={onEdit} data-testid={`l-edit-${lesson.id}`} className="w-8 h-8 flex items-center justify-center border border-[var(--dw-navy)]/12 hover:border-[var(--dw-navy)] rounded-sm text-neutral-500 hover:text-[var(--dw-navy)]">
          <Edit3 className="w-3.5 h-3.5" strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}

function LessonEditorModal({ lessonId, onClose, onSaved }) {
  const [lesson, setLesson] = useState(null);
  const [form, setForm] = useState({ title: "", content: "", duration_min: 15 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/admin/lessons/${lessonId}`);
      setLesson(data);
      setForm({
        title: data.title || "",
        content: data.content || "",
        duration_min: data.duration_min || 15,
      });
    })();
  }, [lessonId]);

  async function save() {
    setSaving(true);
    try {
      await api.put(`/admin/lessons/${lessonId}`, form);
      onSaved();
    } catch (e) {
      alert("Failed to save: " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="l-edit-modal">
      <div className="max-w-4xl w-full h-[90vh] bg-[var(--dw-parchment)] border border-[var(--dw-navy)]/20 rounded-sm shadow-2xl flex flex-col">
        <div className="flex items-center justify-between border-b border-[var(--dw-navy)]/10 px-5 py-3">
          <div>
            <div className="font-serif text-xl text-[var(--dw-navy)]">Editing lesson {lessonId}</div>
            {lesson && <div className="text-xs text-neutral-500 mt-0.5">{TYPE_LABELS[lesson.type] || lesson.type} · Module {lesson.module_id}</div>}
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-neutral-500 hover:text-[var(--dw-navy)]" strokeWidth={1.5} /></button>
        </div>

        {!lesson ? (
          <div className="flex-1 flex items-center justify-center text-neutral-500">Loading lesson…</div>
        ) : (
          <div className="flex-1 overflow-y-auto p-5 space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Title</label>
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="l-field-title"
                className="w-full p-2 text-sm border border-[var(--dw-navy)]/15 rounded-sm focus:outline-none focus:border-[var(--dw-navy)]"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Duration (minutes)</label>
              <input
                type="number"
                value={form.duration_min}
                onChange={(e) => setForm({ ...form, duration_min: parseInt(e.target.value) || 0 })}
                data-testid="l-field-duration"
                className="w-24 p-2 text-sm border border-[var(--dw-navy)]/15 rounded-sm focus:outline-none focus:border-[var(--dw-navy)]"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">
                Content (Markdown supported — # headings, **bold**, tables, lists)
              </label>
              <textarea
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                rows={22}
                data-testid="l-field-content"
                className="w-full p-3 text-sm border border-[var(--dw-navy)]/15 rounded-sm focus:outline-none focus:border-[var(--dw-navy)] font-mono-plex leading-relaxed"
              />
              <div className="text-[10px] text-neutral-500 mt-1">
                {form.content.length.toLocaleString()} characters · ~{Math.ceil(form.content.split(/\s+/).length / 200)} min read
              </div>
            </div>
          </div>
        )}

        <div className="border-t border-[var(--dw-navy)]/10 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="text-sm text-neutral-600 hover:text-[var(--dw-navy)] px-4 py-2">Cancel</button>
          <button onClick={save} disabled={saving || !lesson} data-testid="l-save-btn" className="dw-btn-gold rounded-sm px-5 py-2 text-sm inline-flex items-center gap-1.5 disabled:opacity-60">
            <Save className="w-4 h-4" strokeWidth={1.5} /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
